import React from "react";
export default function MedWellLandingPage() {
    const [isLoginOpen, setIsLoginOpen] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");

    const programs = [
        {
            title: "Telehealth GLP-1 Care",
            text: "Start with a secure video consultation with a licensed doctor. If medically appropriate, receive a prescription for Ozempic or similar GLP-1 treatment online.",
            theme: "glp1",
            img: process.env.PUBLIC_URL + '/telehealth.png',
        },
        {
            title: "Lab-Based Health Planning",
            text: "Visit a nearby partner blood laboratory, complete testing, and receive a doctor-guided plan based on real biomarker data.",
            theme: "labs",
            img: process.env.PUBLIC_URL + '/bloodlabs.png',
        },
        {
            title: "Nutrition & Meal Delivery",
            text: "Add a structured nutrition package with ready-made, balanced meals designed to support better health, improved energy, and sustainable weight loss.",
            theme: "meals",
            img: process.env.PUBLIC_URL + '/mealplans.png'
        },
    ];

    const steps = [
        {
            n: "01",
            title: "Book a video consultation",
            text: "Meet a licensed doctor online to review symptoms, goals, medical history, and treatment eligibility.",
        },
        {
            n: "02",
            title: "Complete blood testing",
            text: "Visit a nearby laboratory in our nationwide network and complete the blood work recommended for your case.",
        },
        {
            n: "03",
            title: "Receive a tailored plan",
            text: "Your doctor reviews the results and creates a personalized health and weight-loss plan, including prescription options when appropriate.",
        },
        {
            n: "04",
            title: "Add meals and follow-up",
            text: "Support your progress with ongoing doctor follow-up, coaching, and optional healthy ready-made meal delivery.",
        },
    ];

    const gallery = [
        {
            title: "Doctor video visit",
            subtitle: "Telehealth consultations focused on safe prescription access and continuous care.",
            theme: "doctor",
            img: process.env.PUBLIC_URL + '/videoconsultation.png'
        },
        {
            title: "Nationwide lab access",
            subtitle: "Structured blood testing and biomarker review as the basis for better decision-making.",
            theme: "lab",
            img: process.env.PUBLIC_URL + '/bloodlabs.png',
        },
        {
            title: "Delicate meal delivery",
            subtitle: "Balanced ready-made meals that fit a sustainable and premium health journey.",
            theme: "meal",
            img: process.env.PUBLIC_URL + '/mealplans.png'
        },
    ];

    function ProgramVisual({ theme }: { theme: string }) {
        if (theme === "glp1") {
            return (
                <svg viewBox="0 0 320 220" className="h-56 w-full" role="img" aria-label="GLP-1 telehealth illustration">
                    <defs>
                        <linearGradient id="progA" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#edf4ee" />
                            <stop offset="100%" stopColor="#d9e8dc" />
                        </linearGradient>
                    </defs>
                    <rect width="320" height="220" fill="url(#progA)" />
                    <rect x="22" y="22" width="176" height="118" rx="22" fill="#ffffff" />
                    <circle cx="239" cy="70" r="34" fill="#efcfbd" />
                    <rect x="207" y="104" width="66" height="58" rx="20" fill="#91b4a3" />
                    <rect x="42" y="42" width="136" height="10" rx="5" fill="#dbe6df" />
                    <rect x="42" y="62" width="102" height="10" rx="5" fill="#dbe6df" />
                    <rect x="208" y="136" width="86" height="14" rx="7" fill="#ffffff" transform="rotate(-26 208 136)" />
                    <rect x="270" y="108" width="18" height="12" rx="5" fill="#efb15d" transform="rotate(-26 270 108)" />
                    <rect x="38" y="154" width="120" height="34" rx="17" fill="#2f7f68" />
                    <text x="98" y="175" textAnchor="middle" fontSize="12" fill="#ffffff">Video consult</text>
                </svg>
            );
        }

        if (theme === "labs") {
            return (
                <svg viewBox="0 0 320 220" className="h-56 w-full" role="img" aria-label="Blood lab illustration">
                    <defs>
                        <linearGradient id="progB" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#f6efe7" />
                            <stop offset="100%" stopColor="#eadfd1" />
                        </linearGradient>
                    </defs>
                    <rect width="320" height="220" fill="url(#progB)" />
                    <rect x="32" y="26" width="256" height="164" rx="26" fill="#ffffff" />
                    <rect x="58" y="50" width="74" height="100" rx="22" fill="#dceddf" />
                    <rect x="84" y="70" width="12" height="54" rx="6" fill="#2f7f68" />
                    <rect x="106" y="62" width="12" height="62" rx="6" fill="#8fb6a4" />
                    <rect x="150" y="60" width="106" height="12" rx="6" fill="#e6ece7" />
                    <rect x="150" y="86" width="90" height="10" rx="5" fill="#e6ece7" />
                    <rect x="150" y="108" width="74" height="10" rx="5" fill="#e6ece7" />
                    <rect x="184" y="144" width="74" height="26" rx="13" fill="#2f7f68" />
                    <text x="221" y="161" textAnchor="middle" fontSize="11" fill="#ffffff">Nearby labs</text>
                </svg>
            );
        }

        return (
            <svg viewBox="0 0 320 220" className="h-56 w-full" role="img" aria-label="Healthy meal delivery illustration">
                <defs>
                    <linearGradient id="progC" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#edf4ee" />
                        <stop offset="100%" stopColor="#d9e6db" />
                    </linearGradient>
                </defs>
                <rect width="320" height="220" fill="url(#progC)" />
                <rect x="36" y="30" width="248" height="162" rx="28" fill="#fffaf4" />
                <ellipse cx="160" cy="118" rx="86" ry="48" fill="#ffffff" stroke="#e4ddd3" strokeWidth="3" />
                <circle cx="128" cy="110" r="20" fill="#83b36f" />
                <circle cx="160" cy="98" r="16" fill="#efad68" />
                <circle cx="192" cy="114" r="22" fill="#6f9f86" />
                <ellipse cx="210" cy="96" rx="18" ry="11" fill="#d7c06f" />
                <rect x="78" y="154" width="124" height="26" rx="13" fill="#2f7f68" />
                <text x="140" y="171" textAnchor="middle" fontSize="11" fill="#ffffff">Meal delivery</text>
            </svg>
        );
    }

    function GalleryVisual({ theme }: { theme: string }) {
        if (theme === "doctor") {
            return (
                <svg viewBox="0 0 320 320" className="h-80 w-full rounded-2xl" role="img" aria-label="Doctor video consultation visual">
                    <defs>
                        <linearGradient id="galA" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#edf4ee" />
                            <stop offset="100%" stopColor="#d7e7db" />
                        </linearGradient>
                    </defs>
                    <rect width="320" height="320" fill="url(#galA)" />
                    <rect x="38" y="48" width="184" height="132" rx="24" fill="#ffffff" />
                    <circle cx="130" cy="104" r="28" fill="#efcfbd" />
                    <rect x="96" y="136" width="68" height="62" rx="22" fill="#9ebead" />
                    <rect x="194" y="220" width="92" height="34" rx="17" fill="#2f7f68" />
                    <text x="240" y="241" textAnchor="middle" fontSize="12" fill="#ffffff">Doctor online</text>
                </svg>
            );
        }

        if (theme === "lab") {
            return (
                <svg viewBox="0 0 320 320" className="h-80 w-full rounded-2xl" role="img" aria-label="Nationwide blood lab visual">
                    <defs>
                        <linearGradient id="galB" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#f5efe7" />
                            <stop offset="100%" stopColor="#eadfd1" />
                        </linearGradient>
                    </defs>
                    <rect width="320" height="320" fill="url(#galB)" />
                    <rect x="50" y="50" width="220" height="220" rx="28" fill="#ffffff" />
                    <circle cx="122" cy="126" r="18" fill="#d9ebe1" />
                    <circle cx="186" cy="96" r="10" fill="#8fb6a4" />
                    <circle cx="212" cy="156" r="14" fill="#2f7f68" />
                    <path d="M122 126 L186 96 L212 156" stroke="#afbeb6" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <rect x="98" y="222" width="130" height="26" rx="13" fill="#2f7f68" />
                    <text x="163" y="239" textAnchor="middle" fontSize="12" fill="#ffffff">Nationwide access</text>
                </svg>
            );
        }

        return (
            <svg viewBox="0 0 320 320" className="h-80 w-full rounded-2xl" role="img" aria-label="Healthy meal package visual">
                <defs>
                    <linearGradient id="galC" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#eef4ef" />
                        <stop offset="100%" stopColor="#d7e5da" />
                    </linearGradient>
                </defs>
                <rect width="320" height="320" fill="url(#galC)" />
                <rect x="42" y="54" width="236" height="212" rx="28" fill="#fffaf4" />
                <ellipse cx="160" cy="168" rx="86" ry="54" fill="#ffffff" stroke="#e4ddd3" strokeWidth="4" />
                <circle cx="126" cy="158" r="22" fill="#83b36f" />
                <circle cx="162" cy="146" r="18" fill="#efad68" />
                <circle cx="198" cy="162" r="24" fill="#6f9f86" />
                <rect x="90" y="88" width="138" height="26" rx="13" fill="#2f7f68" />
                <text x="159" y="105" textAnchor="middle" fontSize="12" fill="#ffffff">Healthy meal plan</text>
            </svg>
        );
    }

    return (
        <div className="min-h-screen bg-[#f7fbf8] text-slate-900">
            <div className="mx-auto max-w-7xl px-5 py-5 lg:px-8">
                <header className="sticky top-4 z-30 flex items-center justify-between rounded-full border border-black/5 bg-white/85 px-5 py-3 shadow-sm backdrop-blur">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-600 text-sm font-semibold text-white">
                            LX
                        </div>
                        <div>
                            <div className="text-sm font-semibold tracking-[0.22em] text-slate-500">LONGEVIX</div>
                            <div className="text-xs text-slate-500">Longevity & regenerative clinic</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsLoginOpen(true)}
                            aria-label="Open login"
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-emerald-200 bg-white text-slate-700 shadow-sm transition hover:bg-emerald-50"
                        >
                            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21a8 8 0 0 0-16 0" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                        </button>
                        <button className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700">
                            Start assessment
                        </button>
                    </div>
                </header>

                <section className="grid gap-8 py-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
                    <div>
                        <div className="mb-4 text-xs tracking-[0.2em] text-slate-400">
                            VIDEO CONSULTATIONS • PRESCRIPTIONS • LABS • MEAL PLANS
                        </div>

                        <h1 className="text-5xl font-semibold leading-[1.02] tracking-tight md:text-6xl">
                            Get prescribed Ozempic online through a licensed video doctor.
                        </h1>

                        <p className="mt-4 max-w-xl text-lg text-slate-600">
                            Video consultation, nationwide lab testing, and a doctor-led weight-loss plan with optional healthy meal delivery.
                        </p>

                        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                            <button className="rounded-full bg-emerald-600 px-7 py-3.5 font-medium text-white shadow-sm transition hover:bg-emerald-700">
                                Check eligibility now
                            </button>
                            <button className="rounded-full border border-emerald-200 bg-white px-7 py-3.5 font-medium text-slate-900 transition hover:bg-emerald-50">
                                Book video consultation
                            </button>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                            <div className="rounded-full bg-emerald-50 px-3 py-1.5 font-medium text-emerald-700">Takes ~2 minutes</div>
                            <div className="rounded-full bg-white px-3 py-1.5 shadow-sm">Licensed doctors</div>
                            <div className="rounded-full bg-white px-3 py-1.5 shadow-sm">Nationwide labs</div>
                            <div className="rounded-full bg-white px-3 py-1.5 shadow-sm">Prescription review online</div>
                        </div>
                    </div>

                    <div className="flex items-start justify-center">
                        <div className="w-full max-w-lg overflow-hidden rounded-[32px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
                            <img src={process.env.PUBLIC_URL + "/overview.png"} alt="" />
                        </div>
                    </div>
                </section>

                <section className="py-10">
                    <div className="mb-8 grid gap-4 rounded-[28px] border border-black/5 bg-white p-5 shadow-sm lg:grid-cols-4">
                        {[
                            ["4.9/5", "Average patient rating"],
                            ["24/7", "Support availability"],
                            ["Nationwide", "Lab access coverage"],
                            ["Doctor-led", "Prescription review"],
                        ].map(([value, label]) => (
                            <div key={label} className="rounded-2xl bg-[#eef7f2] px-4 py-4">
                                <div className="text-2xl font-semibold text-slate-950">{value}</div>
                                <div className="mt-1 text-sm text-slate-600">{label}</div>
                            </div>
                        ))}
                    </div>

                    <h2 className="mb-6 text-3xl font-semibold">Core Programs</h2>
                    <p className="mb-8 max-w-3xl text-slate-600">
                        The model is built around three connected services: video consultations with doctors, blood-test-informed treatment planning, and optional meal delivery to support healthier habits and better weight-loss outcomes.
                    </p>

                    <div className="grid gap-5 md:grid-cols-3">
                        {programs.map((item) => (
                            <div key={item.title} className="overflow-hidden rounded-2xl bg-white shadow-sm">
                                <img src={item.img} alt={item.title} />
                                <div className="p-5">
                                    <h3 className="text-xl font-semibold">{item.title}</h3>
                                    <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="grid gap-6 py-10 lg:grid-cols-2">
                    <div>
                        <h2 className="text-3xl font-semibold">How the model works</h2>
                        <p className="mt-4 text-slate-600">
                            Patients start with a doctor by video, complete blood testing through a nationwide laboratory network, and then receive a tailored plan that may include prescription treatment, nutrition guidance, and ready-made healthy meals.
                        </p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        {steps.map((step) => (
                            <div key={step.n} className="rounded-xl bg-white p-5 shadow-sm">
                                <div className="text-sm text-slate-400">{step.n}</div>
                                <h3 className="mt-2 font-semibold">{step.title}</h3>
                                <p className="mt-2 text-sm text-slate-600">{step.text}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="py-10">
                    <h2 className="mb-6 text-3xl font-semibold">Visual direction</h2>
                    <div className="grid gap-5 md:grid-cols-3">
                        {gallery.map((item) => (
                            <div key={item.title}>
                                <img src={item.img} alt={item.title} />
                                <div className="mt-3">
                                    <div className="font-semibold">{item.title}</div>
                                    <div className="text-sm text-slate-600">{item.subtitle}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="py-10">
                    <div className="rounded-[28px] bg-gradient-to-br from-[#e9f7ef] via-white to-[#f0fbf6] p-6 shadow-sm">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Ready to start</div>
                                <h2 className="mt-2 text-3xl font-semibold text-slate-950">Answer a few questions and see if you qualify.</h2>
                                <p className="mt-2 max-w-2xl text-slate-600">
                                    A short eligibility flow helps match patients with the right consultation, lab work, and treatment path.
                                </p>
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <button className="rounded-full bg-emerald-600 px-6 py-3.5 font-medium text-white transition hover:bg-emerald-700">Check eligibility</button>
                                <button className="rounded-full border border-emerald-200 bg-white px-6 py-3.5 font-medium text-slate-900 transition hover:bg-emerald-50">Book doctor now</button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-12">
                    <div className="flex flex-col items-center justify-between rounded-2xl bg-emerald-600 p-8 text-white lg:flex-row">
                        <div>
                            <h2 className="text-3xl font-semibold">Go beyond one-size-fits-all weight loss.</h2>
                            <p className="mt-3 text-white/90">
                                Video consultations, prescriptions, nationwide labs, and healthy meal delivery in one structured care model.
                            </p>
                        </div>
                        <button className="mt-4 rounded-full bg-white px-6 py-3 text-black lg:mt-0">
                            Book consultation
                        </button>
                    </div>
                </section>

                {isLoginOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-[0_30px_80px_rgba(15,23,42,0.22)]">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Customer login</div>
                                    <h3 className="mt-2 text-2xl font-semibold text-slate-950">Sign in to your account</h3>
                                    <p className="mt-2 text-sm leading-6 text-slate-600">
                                        Access your consultations, lab results, prescriptions, and care plan.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsLoginOpen(false)}
                                    aria-label="Close login"
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50"
                                >
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                        <path d="M6 6l12 12M18 6L6 18" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mt-6 space-y-4">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
                                    />
                                </div>
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
                                    />
                                </div>
                                <button className="w-full rounded-full bg-emerald-600 px-6 py-3.5 font-medium text-white transition hover:bg-emerald-700">
                                    Sign in
                                </button>
                            </div>

                            <div className="mt-5 rounded-2xl bg-[#eef7f2] px-4 py-4">
                                <div className="text-sm font-medium text-slate-900">Not a customer yet?</div>
                                <div className="mt-1 text-sm leading-6 text-slate-600">
                                    Register to book a video consultation, complete your intake, and start your personalized plan.
                                </div>
                                <button className="mt-3 rounded-full border border-emerald-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition hover:bg-emerald-50">
                                    Register
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
